import React, { useState } from "react";
import { toast } from "react-toastify";
import { api, API_BASE_URL } from "../../config/api";
import { Form, Input, Select, Button, Typography, Switch } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, HomeOutlined, GlobalOutlined, NumberOutlined, BankOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const { Title } = Typography;

const currencies = [
  { currency: "USD", symbol: "$" },
  { currency: "EUR", symbol: "€" },
];

const AddUserForm = ({ setOpenUserModel }) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [form] = Form.useForm();

  const validatePostalCode = (_, value) => {
    if (!value || /^\d+$/.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Postal code should contain only numbers"));
  };

  const validatePassword = (_, value) => {
    if (!value) {
      return Promise.reject(new Error("Please enter a password"));
    }
    if (!/^(?=.*[!@#$%^&*(),.?":{}|<>])(?=.*\d).{6,20}$/.test(value)) {
      return Promise.reject(new Error("Password must be 6-20 characters long and contain at least one special character and one number"));
    }
    return Promise.resolve();
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const userData = { ...values, role: "user" };

      const res = await api.post("/users", userData);
      if (res.status === 201) {
        let { uid } = res.data;

        // Only send welcome email if the toggle is enabled
        if (sendWelcomeEmail) {
          try {
            await api.post(`${API_BASE_URL}/mail/send-welcome-email`, userData);
            toast.success("User created and welcome email sent successfully!");
          } catch (emailError) {
            console.error("Error sending welcome email:", emailError);
            toast.warning("User created but failed to send welcome email");
          }
        } else {
          toast.success("User created successfully!");
        }

        setOpenUserModel(false);
        queryClient.invalidateQueries(["users"]);
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error(
        error?.response?.data?.message
          ? error?.response?.data?.message
          : "User not added"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="md:w-[500px] w-full">
      <div className="flex justify-center mb-4">
        <Title level={3} className="m-0">Add User</Title>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ role: "user" }}
        className="mt-3 w-full"
      >
        <div className="space-y-4">
          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>Name:
            </label>
            <Form.Item
              name="name"
              rules={[{ required: true, message: 'Please enter name' }]}
              className="flex-1 mb-0"
            >
              <Input prefix={<UserOutlined />} placeholder="Enter Name" />
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>Email:
            </label>
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
              className="flex-1 mb-0"
            >
              <Input prefix={<MailOutlined />} placeholder="Enter Email" />
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>Password:
            </label>
            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please enter password' },
                { validator: validatePassword }
              ]}
              className="flex-1 mb-0"
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Enter Password" />
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>Address:
            </label>
            <Form.Item
              name="address"
              rules={[{ required: true, message: 'Please enter address' }]}
              className="flex-1 mb-0"
            >
              <Input prefix={<HomeOutlined />} placeholder="Enter Address" />
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>City:
            </label>
            <Form.Item
              name="city"
              rules={[{ required: true, message: 'Please enter city' }]}
              className="flex-1 mb-0"
            >
              <Input prefix={<HomeOutlined />} placeholder="Enter City" />
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>Country:
            </label>
            <Form.Item
              name="country"
              rules={[{ required: true, message: 'Please enter country' }]}
              className="flex-1 mb-0"
            >
              <Input prefix={<GlobalOutlined />} placeholder="Enter Country" />
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>Postal Code:
            </label>
            <Form.Item
              name="postalCode"
              rules={[
                { required: true, message: 'Please enter postal code' },
                // { validator: validatePostalCode }
              ]}
              className="flex-1 mb-0"
            >
              <Input prefix={<NumberOutlined />} placeholder="Enter Postal Code" />
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">VAT Number:</label>
            <Form.Item
              name="vatNumber"
              className="flex-1 mb-0"
            >
              <Input prefix={<BankOutlined />} placeholder="Enter VAT Number (Optional)" />
            </Form.Item>
          </div>

          <div className="flex items-start">
            <label className="w-[120px] pt-2 font-medium">Bill To:</label>
            <Form.Item
              name="billToText"
              className="flex-1 mb-0"
            >
              <Input.TextArea rows={3} placeholder="Enter bill-to details" />
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>Currency:
            </label>
            <Form.Item
              name="currency"
              rules={[{ required: true, message: 'Please select currency' }]}
              className="flex-1 mb-0"
            >
              <Select
                placeholder="Select Currency"
                showSearch
                optionFilterProp="children"
              >
                {currencies.map(({ currency, symbol }) => (
                  <Select.Option key={currency} value={currency}>
                    {symbol} {currency}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">Send email:</label>
            <Form.Item className="flex-1 mb-0">
              <Switch
                defaultChecked
                onChange={(checked) => setSendWelcomeEmail(checked)}
                checkedChildren={<MailOutlined />}
                unCheckedChildren={<MailOutlined />}
              />
              <span className="ml-2 text-sm text-gray-500">
                {sendWelcomeEmail ? "Yes, send welcome email with credentials" : "No, skip welcome email"}
              </span>
            </Form.Item>
          </div>

          <div className="flex justify-end">
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="bg-blue-600 hover:bg-blue-700"
              shape="round"
              size="large"
              style={{ width: '200px' }}
            >
              {loading ? "Adding..." : "Add User"}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default AddUserForm;
